import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  Shield, 
  Droplets, 
  Flame, 
  Clock, 
  Award,
  ArrowRight,
  Factory,
  Hand,
  AlertTriangle,
  ThumbsUp,
  Download
} from 'lucide-react';
import BrochureDownloadModal from '../components/BrochureDownloadModal';

const PURTechnology = () => {
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);

  const comparisonData = [
    {
      feature: 'Bonding Method',
      manual: 'Hand-applied with brush/roller',
      pur: 'Precision machine application',
      manualIcon: Hand,
      purIcon: Factory
    },
    {
      feature: 'Bond Consistency',
      manual: 'Uneven, depends on worker skill',
      pur: '100% uniform across entire surface',
      manualIcon: XCircle,
      purIcon: CheckCircle
    },
    {
      feature: 'Air Bubbles',
      manual: 'Common problem, wavy surface',
      pur: 'Zero bubbles guaranteed',
      manualIcon: XCircle,
      purIcon: CheckCircle
    },
    {
      feature: 'Heat Resistance',
      manual: 'Glue softens at 50-60°C, laminate lifts near stove',
      pur: 'Withstands up to 150°C - perfect for kitchens',
      manualIcon: XCircle,
      purIcon: CheckCircle
    },
    {
      feature: 'Water/Moisture Resistance',
      manual: 'Water-based glue fails in humidity',
      pur: '100% waterproof bond - ideal for bathrooms',
      manualIcon: XCircle,
      purIcon: CheckCircle
    },
    {
      feature: 'Edge Bonding',
      manual: 'Edges lift within 1-2 years',
      pur: 'Permanent edge bond - no lifting ever',
      manualIcon: XCircle,
      purIcon: CheckCircle
    },
    {
      feature: 'Drying/Curing Time',
      manual: '24-48 hours clamping required',
      pur: 'Instant bond, ready to use',
      manualIcon: Clock,
      purIcon: CheckCircle
    },
    {
      feature: 'Product Lifespan',
      manual: '3-5 years before problems appear',
      pur: '15+ years guaranteed performance',
      manualIcon: XCircle,
      purIcon: Award
    }
  ];

  const problems = [
    {
      title: 'Bubbles & Uneven Surface',
      description: 'Manual glue application creates air pockets and uneven surfaces. Your expensive laminate looks cheap with a wavy finish.',
      hindiText: '"Itna paisa diya, phir bhi finish smooth nahi hai"',
      icon: AlertTriangle
    },
    {
      title: 'Edge Lifting in 1-2 Years',
      description: 'Water-based adhesive weakens at edges first. Kitchen steam and bathroom humidity cause corners to peel.',
      hindiText: '"Sirf 2 saal mein kitchen kharab ho gaya"',
      icon: AlertTriangle
    },
    {
      title: 'Laminate Lifting Near Gas Stove',
      description: 'Water-based glue cannot handle cooking heat. Laminate near burners starts bubbling and lifting.',
      hindiText: '"Gas ke paas laminate uth gaya"',
      icon: Flame
    },
    {
      title: 'Bathroom & Sink Area Failure',
      description: 'Water splashes weaken the glue bond. Laminate swells, edges lift, and the board underneath starts rotting.',
      hindiText: '"Bathroom door 6 mahine mein kharab"',
      icon: Droplets
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              EXCLUSIVE TECHNOLOGY
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              CriLam PUR-Bond™ Lamination
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              Kanpur&apos;s First PUR Lamination Facility
            </p>
            <p className="text-lg text-white/70 max-w-2xl mb-8">
              Factory-laminated boards with permanent, heat-resistant, waterproof bonding. 
              No more edge lifting. No more bubbles. No more failures.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <motion.button
                  className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get PUR-Bonded Products
                  <ArrowRight className="h-4 w-4" />
                </motion.button>
              </Link>
              <button
                onClick={() => setBrochureModalOpen(true)}
                className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Technical Brochure
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is PUR Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="label-mono text-[#D4AF37] mb-4">What is PUR?</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6 font-['Outfit']">
                Polyurethane Reactive (PUR) Lamination
              </h2>
              <div className="space-y-4 text-[#525956]">
                <p>
                  PUR lamination is an advanced industrial bonding technology used by premium manufacturers worldwide. 
                  Unlike traditional water-based glue applied manually by carpenters, PUR adhesive is applied by 
                  precision machines and creates a <strong>permanent, chemical bond</strong> that never fails.
                </p>
                <p>
                  The PUR adhesive reacts with moisture in the air to cure, creating an incredibly strong bond 
                  that is <strong>heat-resistant up to 150°C</strong> and <strong>100% waterproof</strong>. 
                  Once cured, it cannot be reversed or weakened.
                </p>
                <p className="text-[#004534] font-semibold">
                  CriLam Decorative operates Kanpur&apos;s first PUR lamination facility — 
                  giving you access to European-quality laminated products made in India.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="/pur-lamination-machine.png"
                alt="CriLam PUR Lamination Machine"
                className="w-full h-auto rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#004534] text-white p-6 max-w-xs">
                <p className="text-[#D4AF37] font-bold text-lg mb-2">10-Year Guarantee</p>
                <p className="text-sm text-white/80">No edge lifting. No delamination. Guaranteed.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems with Manual Pasting */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">The Problem</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
              Problems with Manual Water-Based Glue Pasting
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              When carpenters manually paste laminate using water-based adhesive, these problems are inevitable
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-8 bg-white/[0.03] border border-red-500/20 hover:border-red-500/40 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 flex-shrink-0">
                    <problem.icon className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{problem.title}</h3>
                    <p className="text-white/60 mb-3">{problem.description}</p>
                    <p className="text-[#D4AF37] italic text-sm">"{problem.hindiText}"</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">The Comparison</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6 font-['Outfit']">
              Manual Pasting vs CriLam PUR-Bond™ Technology
            </h2>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left bg-[#F7F8F5] border-b-2 border-[#004534]">Feature</th>
                  <th className="p-4 text-left bg-red-50 border-b-2 border-red-300">
                    <div className="flex items-center gap-2">
                      <Hand className="h-5 w-5 text-red-500" />
                      <span className="text-red-700">Manual Water-Based Glue</span>
                    </div>
                  </th>
                  <th className="p-4 text-left bg-green-50 border-b-2 border-green-500">
                    <div className="flex items-center gap-2">
                      <Factory className="h-5 w-5 text-green-600" />
                      <span className="text-green-700">CriLam PUR-Bond™ Technology</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <motion.tr 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-gray-200"
                  >
                    <td className="p-4 font-medium text-[#004534]">{row.feature}</td>
                    <td className="p-4 bg-red-50/50">
                      <div className="flex items-start gap-2">
                        <row.manualIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-red-700">{row.manual}</span>
                      </div>
                    </td>
                    <td className="p-4 bg-green-50/50">
                      <div className="flex items-start gap-2">
                        <row.purIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-green-700">{row.pur}</span>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits by Application */}
      <section className="py-20 lg:py-28 bg-[#004534] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Applications</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
              CriLam PUR-Bond™ Technology Benefits by Application
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Modular Kitchens',
                icon: Flame,
                benefits: [
                  'Heat-resistant near gas stove (150°C)',
                  'Waterproof near sink areas',
                  'No edge lifting from steam',
                  'Easy to clean, chemical resistant'
                ],
                guarantee: 'Kitchen will look new even after 15 years of daily cooking'
              },
              {
                title: 'Wardrobes & Furniture',
                icon: Shield,
                benefits: [
                  'No bubbles, perfectly smooth finish',
                  'Edges never lift, even in humidity',
                  'Premium European-quality look',
                  'Consistent quality across all pieces'
                ],
                guarantee: 'Premium finish that lasts a lifetime'
              },
              {
                title: 'Doors & Wet Areas',
                icon: Droplets,
                benefits: [
                  '100% waterproof for bathrooms',
                  'AC room temperature changes - no problem',
                  'Balcony doors - weather resistant',
                  'No rotting, no swelling'
                ],
                guarantee: 'Doors that perform in every room'
              }
            ].map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="p-8 bg-white/[0.05] border border-white/[0.1]"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37] mb-6">
                  <app.icon className="h-7 w-7 text-[#004534]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{app.title}</h3>
                <ul className="space-y-3 mb-6">
                  {app.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80">
                      <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[#D4AF37] font-medium italic">"{app.guarantee}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 10-Year Guarantee */}
      <section className="py-20 lg:py-28 bg-[#D4AF37]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Award className="h-20 w-20 text-[#004534] mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-6 font-['Outfit']">
                10-Year No-Delamination Guarantee
              </h2>
              <p className="text-xl text-[#004534]/80 mb-8">
                We're so confident in our PUR lamination technology that we offer a 10-year written guarantee. 
                If any CriLam PUR-bonded product shows edge lifting or delamination within 10 years, 
                we'll replace it free of cost.
              </p>
              <p className="text-lg text-[#004534] font-semibold">
                No water-based glue pasting can offer this. Only CriLam PUR can.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Get Started
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Switch to PUR-Bonded Products Today
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Contact us for product samples, dealer information, or technical consultation
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/contact">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/dealer">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Become a Dealer
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brochure Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </div>
  );
};

export default PURTechnology;
